import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	useNavigate,
	useParams,
	useRouterState,
} from "@tanstack/react-router";
import {
	Calendar,
	CalendarCheck01,
	Hash01,
	Minimize01,
	Trash01,
} from "@untitledui/icons";
import { useCallback, useMemo, useState } from "react";
import { DeleteRecordModal } from "@/components/Organisms/DeleteRecordModal/DeleteRecordModal";
import {
	DetailsState,
	DetailsSurface,
} from "@/components/Organisms/Details/Details";
import {
	PageHeader,
	type PageHeaderTab,
} from "@/components/Organisms/PageHeader/PageHeader";
import { PageLayout } from "@/components/Organisms/PageLayout/PageLayout";
import { Sidebar } from "@/components/Organisms/Sidebar/Sidebar";
import { commitAutosaveOrStay } from "@/features/entity-details";
import { useJobDetailsSaveFailLabels } from "@/features/jobs/hooks/use-job-details-navigation-guard";
import {
	RecordDetailsTab,
	RecordEntityDetails,
} from "@/features/records/components";
import {
	RecordFormSaveProvider,
	useRecordFormSaveRegistry,
} from "@/features/records/components/record-form-save-context";
import { useRecordFormNavigationGuard } from "@/features/records/hooks/use-record-form-navigation-guard";
import {
	getOrgRecordDrawerDetailsPath,
	getOrgRecordWorkspacePath,
} from "@/features/records/lib/get-org-record-workspace-path.util";
import {
	getOrgRecordWorkspaceTabId,
	OrgRecordWorkspaceTab,
	type OrgRecordWorkspaceTabId,
} from "@/features/records/lib/get-org-record-workspace-tab-id.util";
import { getRecordDisplayTitle } from "@/features/records/lib/get-record-display-title.util";
import { isNestedAssetUnderRecordWorkspacePath } from "@/features/records/lib/is-nested-asset-under-record-workspace-path.util";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { authQueryOptions } from "@/services/auth";
import { recordInstanceQueryOptions } from "@/services/record";
import { cx } from "@/utils/cx";
import {
	createdMetadataLabel,
	updatedMetadataLabel,
} from "@/utils/entity-metadata-date-label";

/**
 * Top-level org Record Workspace shell — Landing → Drawer → Workspace.
 * Header: PageHeader with section tabs, Options overflow (delete), and collapse back to drawer.
 * Nested Asset workspace under Assets yields this shell (ADO #1564).
 */
export const Route = createFileRoute(
	"/_authenticated/(web-app)/_web/web/$orgId/records/(record)/$recordId/workspace",
)({
	component: RecordWorkspaceLayout,
});

function RecordWorkspaceLayout() {
	const pathname = useRouterState({
		select: (state) => state.location.pathname,
	});

	if (isNestedAssetUnderRecordWorkspacePath(pathname)) {
		return <Outlet />;
	}

	return (
		<RecordFormSaveProvider>
			<RecordWorkspaceLayoutInner />
		</RecordFormSaveProvider>
	);
}

function RecordWorkspaceLayoutInner() {
	const { t } = useAppTranslation();
	const navigate = useNavigate();
	const params = useParams({
		from: "/_authenticated/(web-app)/_web/web/$orgId/records/(record)/$recordId/workspace",
	});
	const pathname = useRouterState({
		select: (state) => state.location.pathname,
	});
	const selectedTabId = getOrgRecordWorkspaceTabId(pathname);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [isSidebarOpen, setIsSidebarOpen] = useState(true);

	const formSaveRegistry = useRecordFormSaveRegistry();
	const saveFailLabels = useJobDetailsSaveFailLabels();
	useRecordFormNavigationGuard();

	const runWithFormCommit = useCallback(
		async (action: () => void) => {
			const ctrl = formSaveRegistry?.getController() ?? null;
			if (ctrl?.hasPending()) {
				const shouldStay = await commitAutosaveOrStay(
					ctrl.commitPending,
					saveFailLabels,
				);
				if (shouldStay) return;
			}
			action();
		},
		[formSaveRegistry, saveFailLabels],
	);

	const recordQuery = useQuery(
		recordInstanceQueryOptions.detail(params.recordId, {
			organizationId: params.orgId,
		}),
	);

	const canDeleteRecord = useQuery({
		...authQueryOptions.checkAction({
			name: "DeleteRecord",
			entityType: "Record",
			entityId: params.recordId,
		}),
		enabled: !!params.recordId,
	});

	const title = getRecordDisplayTitle({
		name: recordQuery.data?.name,
		recordNumber: recordQuery.data?.recordNumber,
	});

	const tabs: PageHeaderTab[] = useMemo(
		() => [
			{
				id: OrgRecordWorkspaceTab.Form,
				label: t("tickets.landing.sections.form"),
			},
			{
				id: OrgRecordWorkspaceTab.Assets,
				label: t("tickets.landing.sections.assets"),
			},
			{
				id: OrgRecordWorkspaceTab.Attachments,
				label: t("tickets.landing.sections.attachments"),
			},
			{
				id: OrgRecordWorkspaceTab.Notes,
				label: t("tickets.landing.sections.notes"),
			},
			{
				id: OrgRecordWorkspaceTab.Map,
				label: t("tickets.landing.sections.map"),
			},
		],
		[t],
	);

	const metadata = useMemo(() => {
		const record = recordQuery.data;
		if (!record) return [];

		const items: {
			label: string;
			icon: typeof Hash01;
		}[] = [];

		if (record.recordNumber != null) {
			items.push({
				label: String(record.recordNumber),
				icon: Hash01,
			});
		}
		if (record.createdAt) {
			items.push({
				label: createdMetadataLabel(t, record.createdAt),
				icon: Calendar,
			});
		}
		if (record.updatedAt) {
			items.push({
				label: updatedMetadataLabel(t, record.updatedAt),
				icon: CalendarCheck01,
			});
		}
		return items;
	}, [recordQuery.data, t]);

	const handleBackToRecords = () => {
		void runWithFormCommit(() => {
			navigate({
				to: "/web/$orgId/records",
				params: { orgId: params.orgId },
				search: {},
			});
		});
	};

	const handleCollapseWorkspace = () => {
		void runWithFormCommit(() => {
			navigate({
				href: getOrgRecordDrawerDetailsPath({
					orgId: params.orgId,
					recordId: params.recordId,
				}),
			});
		});
	};

	const handleTabChange = (tabId: string) => {
		const next = tabId as OrgRecordWorkspaceTabId;
		if (next === selectedTabId) return;

		void runWithFormCommit(() => {
			navigate({
				href: getOrgRecordWorkspacePath({
					orgId: params.orgId,
					recordId: params.recordId,
					tab: next,
				}),
			});
		});
	};

	const handleDeleteSuccess = () => {
		setIsDeleteModalOpen(false);
		void navigate({
			to: "/web/$orgId/records",
			params: { orgId: params.orgId },
			search: {},
			replace: true,
		});
	};

	const sidebarLabel = t(
		"main.tickets.workspace.records.workspace.sidebarLabel",
	);

	const sidebarTabs = useMemo(
		() => [
			{
				label: t("main.tickets.workspace.records.drawer.tabs.details.label"),
				id: RecordDetailsTab.Details,
			},
			{
				label: t("main.tickets.workspace.records.drawer.tabs.activity.label"),
				id: RecordDetailsTab.Activity,
			},
		],
		[t],
	);

	return (
		<div className="relative flex min-h-0 w-full flex-1 overflow-hidden">
			<div
				className={cx(
					"flex min-h-0 min-w-0 flex-1 flex-col transition-[padding] duration-300 ease-in-out motion-reduce:transition-none",
					isSidebarOpen && "lg:pr-[440px]",
				)}
			>
				<PageLayout
					header={
						<PageHeader
							type="record"
							title={title}
							breadcrumbType="single"
							breadcrumbs={[
								{
									label: t("main.nav.items.records.label"),
									onPress: handleBackToRecords,
								},
							]}
							metadata={metadata.length > 0 ? metadata : undefined}
							overflowAriaLabel={t("common.tableActions.options")}
							overflowActions={
								canDeleteRecord.data !== false
									? [
											{
												label: t(
													"main.tickets.workspace.records.drawer.delete.action",
												),
												icon: Trash01,
												onPress: () => setIsDeleteModalOpen(true),
											},
										]
									: []
							}
							actions={[
								{
									label: t("main.tickets.workspace.action"),
									"aria-label": t(
										"main.tickets.workspace.records.workspace.collapse",
									),
									icon: Minimize01,
									color: "secondary",
									onPress: handleCollapseWorkspace,
								},
							]}
							showTabs
							tabs={tabs}
							selectedTabId={selectedTabId}
							onTabChange={handleTabChange}
						/>
					}
				>
					<Outlet />
				</PageLayout>
			</div>
			<Sidebar
				isOpen={isSidebarOpen}
				onOpenChange={setIsSidebarOpen}
				aria-label={sidebarLabel}
				className="max-lg:hidden"
			>
				<RecordEntityDetails
					orgId={params.orgId}
					recordId={params.recordId}
					surface={DetailsSurface.Tertiary}
					state={
						recordQuery.isPending ? DetailsState.Loading : DetailsState.Default
					}
					tabs={sidebarTabs}
					data-testid="org-record-workspace-sidebar-details"
				/>
			</Sidebar>
			<DeleteRecordModal
				isOpen={isDeleteModalOpen}
				setIsOpen={setIsDeleteModalOpen}
				handleClose={() => setIsDeleteModalOpen(false)}
				onDeleteSuccess={handleDeleteSuccess}
				recordId={params.recordId}
				organizationId={params.orgId}
			/>
		</div>
	);
}
